import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { motion } from 'framer-motion';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';

const formSchema = z.object({
  firstName: z.string().min(2, "First name is required"),
  lastName: z.string().min(2, "Last name is required"),
  email: z.string().email("Valid email is required"),
  phone: z.string().min(10, "Valid phone number is required"),
  creditScore: z.string().min(1, "Please select credit profile"),
  interest: z.string().min(1, "Please select equipment type"),
  yearStarted: z.string().min(4, "Please select year"),
});

export function LeadForm() {
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const startedRef = useRef(false);

  function handleFormStarted() {
    if (startedRef.current) return;
    startedRef.current = true;
    trackEvent('lead_form_started');
  }

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      creditScore: "",
      interest: "",
      yearStarted: "",
    },
  });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1969 }, (_, i) => (currentYear - i).toString());

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    setIsError(false);
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      if (!res.ok) {
        throw new Error(`Server error: ${res.status}`);
      }
      setIsSuccess(true);
      trackEvent('lead_form_submitted');
    } catch (err) {
      console.error('Lead form submission failed', err);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }

  if (isSuccess) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white border border-gray-200 rounded-lg p-8 md:p-12 text-center h-full flex flex-col justify-center items-center shadow-sm"
      >
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
          <CheckCircle2 className="w-10 h-10 text-green-600" />
        </div>
        <h3 className="text-3xl font-bold text-[#1a2f36] mb-4">Application Received</h3>
        <p className="text-slate-600 text-lg max-w-md mx-auto">
          Thank you for applying. A finance specialist will review your information and contact you within 2 business hours.
        </p>
        <Button 
          className="mt-8 bg-[#1a2f7e] hover:bg-[#152568] text-white"
          onClick={() => {
            form.reset();
            setIsSuccess(false);
          }}
        >
          Submit Another Request
        </Button>
      </motion.div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 md:p-8 shadow-sm">
      <div className="mb-6">
        <h2 className="text-2xl font-extrabold text-[#1a2f36] uppercase tracking-tight leading-tight">Request Financing</h2>
        <p className="text-slate-500 text-sm mt-1">Hear from a specialist in 2 hours.</p>
      </div>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} onFocus={handleFormStarted} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#1a2f36]/80 font-semibold">First Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John" className="bg-white border-gray-300 text-[#1a2f36] placeholder:text-gray-400 h-12" {...field} />
                  </FormControl>
                  <FormMessage className="text-[#cc2020]" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#1a2f36]/80 font-semibold">Last Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Doe" className="bg-white border-gray-300 text-[#1a2f36] placeholder:text-gray-400 h-12" {...field} />
                  </FormControl>
                  <FormMessage className="text-[#cc2020]" />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#1a2f36]/80 font-semibold">Email Address</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="john@example.com" className="bg-white border-gray-300 text-[#1a2f36] placeholder:text-gray-400 h-12" {...field} />
                  </FormControl>
                  <FormMessage className="text-[#cc2020]" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#1a2f36]/80 font-semibold">Phone Number</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="(555) 123-4567" className="bg-white border-gray-300 text-[#1a2f36] placeholder:text-gray-400 h-12" {...field} />
                  </FormControl>
                  <FormMessage className="text-[#cc2020]" />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="creditScore"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[#1a2f36]/80 font-semibold">How's Your Credit?</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="bg-white border-gray-300 text-[#1a2f36] h-12">
                      <SelectValue placeholder="Select Credit Profile" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-white border-gray-200 text-[#1a2f36]">
                    <SelectItem value="excellent">Excellent Credit 700+</SelectItem>
                    <SelectItem value="good">Good Credit 640-699</SelectItem>
                    <SelectItem value="challenged">Challenged Credit 550-639</SelectItem>
                    <SelectItem value="damaged">Damaged Credit &lt;549</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage className="text-[#cc2020]" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="interest"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[#1a2f36]/80 font-semibold">Interested In Financing</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="bg-white border-gray-300 text-[#1a2f36] h-12">
                      <SelectValue placeholder="Select Equipment Type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-white border-gray-200 text-[#1a2f36]">
                    <SelectItem value="semi">Semi-Truck</SelectItem>
                    <SelectItem value="trailer">Trailer</SelectItem>
                    <SelectItem value="tow">Tow Truck</SelectItem>
                    <SelectItem value="box">Box Truck</SelectItem>
                    <SelectItem value="construction">Construction Equipment</SelectItem>
                    <SelectItem value="vocational">Light and Medium Duty</SelectItem>
                    <SelectItem value="ambulance">Ambulance</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage className="text-[#cc2020]" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="yearStarted"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[#1a2f36]/80 font-semibold">Year Business Started</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="bg-white border-gray-300 text-[#1a2f36] h-12">
                      <SelectValue placeholder="Select Year" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-white border-gray-200 text-[#1a2f36] max-h-[300px]">
                    {years.map((year) => (
                      <SelectItem key={year} value={year}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage className="text-[#cc2020]" />
              </FormItem>
            )}
          />

          <Button 
            type="submit" 
            className="w-full bg-[#cc2020] hover:bg-[#a81a1a] text-white font-bold h-14 text-lg rounded-sm mt-6 uppercase tracking-wider"
            disabled={isLoading}
            data-testid="btn-submit-lead"
          >
            {isLoading ? <Loader2 className="w-6 h-6 animate-spin" /> : "LET'S GO"}
          </Button>

          {isError && (
            <div
              role="alert"
              className="mt-3 flex items-start gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              <span className="mt-0.5 shrink-0">⚠️</span>
              <span>
                Something went wrong — please try again or call us at{' '}
                <a href="tel:18664410513" className="font-semibold underline underline-offset-2">
                  1-866-441-0513
                </a>
              </span>
            </div>
          )}
        </form>
      </Form>
    </div>
  );
}
